import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../lib/auth';
import {
  addSelection,
  removeSelection,
  buildImportPayload,
  type SelectedFolder,
} from '../lib/sharepoint';

// ---------------------------------------------------------------------------
// Types (mirror the backend Graph shapes returned by sharepoint-browse)
// ---------------------------------------------------------------------------

export interface SharePointSite {
  id: string;
  name: string;
  displayName: string;
  webUrl: string;
}

export interface SharePointDrive {
  id: string;
  name: string;
  driveType?: string;
}

export interface SharePointItem {
  id: string;
  name: string;
  kind: 'folder' | 'file';
  size?: number;
  contentType?: string;
  childCount?: number;
  driveId: string;
}

/** Progress totals returned by the import status endpoint. */
export interface ImportTotals {
  discovered: number;
  imported: number;
  skipped: number;
  failed: number;
  /** Files already present in the target folder (idempotency guard / replay); NOT imported. */
  alreadyPresent: number;
}

export type ImportState = 'queued' | 'running' | 'completed' | 'failed';

export interface ImportFailureEntry {
  name: string;
  reason: string;
}

/** Full job status (GET /sharepoint/imports/{jobId}). */
export interface ImportJobStatus {
  jobId: string;
  state: ImportState;
  totals: ImportTotals;
  failureSummary?: ImportFailureEntry[];
  rolledUpFailures?: Record<string, number>;
  failureReason?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Summary job entry (GET /sharepoint/imports). */
export interface ImportJobSummary {
  jobId: string;
  state: ImportState;
  totals: ImportTotals;
  createdAt?: string;
  updatedAt?: string;
  failureReason?: string;
}

/** A key identifying a children listing: `driveId` (root) or `driveId:itemId`. */
type ChildrenKey = string;

export function childrenKey(driveId: string, itemId?: string): ChildrenKey {
  return itemId ? `${driveId}:${itemId}` : driveId;
}

/** Terminal job states stop polling. */
export function isTerminalState(state: ImportState): boolean {
  return state === 'completed' || state === 'failed';
}

/** localStorage key under which the current running import job id is persisted. */
export const CURRENT_JOB_STORAGE_KEY = 'sharepoint.currentJobId';

/** Persist (or clear when undefined) the current running job id so a refresh /
 * navigate-away-and-return can re-attach the live progress card. */
function persistCurrentJobId(jobId: string | undefined): void {
  try {
    if (jobId) {
      localStorage.setItem(CURRENT_JOB_STORAGE_KEY, jobId);
    } else {
      localStorage.removeItem(CURRENT_JOB_STORAGE_KEY);
    }
  } catch {
    // localStorage unavailable (private mode / SSR) — persistence is best-effort.
  }
}

/** Read the persisted current job id, or undefined when absent/unavailable. */
export function readPersistedJobId(): string | undefined {
  try {
    return localStorage.getItem(CURRENT_JOB_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface SharePointState {
  // Browser tree, cached per key.
  sites: SharePointSite[] | null;
  drivesBySite: Record<string, SharePointDrive[]>;
  childrenByKey: Record<ChildrenKey, SharePointItem[]>;

  // Per-section loading flags / errors.
  sitesLoading: boolean;
  sitesError: string | null;
  drivesLoading: boolean;
  drivesError: string | null;
  childrenLoading: boolean;
  childrenError: string | null;

  /** Non-fatal per-request inaccessible warning (R3.5). */
  itemWarning: string | null;
  /** Configuration error (409 CONFIG_ERROR) — credentials/consent missing (R1.3/1.4). */
  configError: string | null;

  // Selection of import roots.
  selection: SelectedFolder[];

  // Imports.
  jobsById: Record<string, ImportJobStatus>;
  jobList: ImportJobSummary[];
  currentJobId?: string;
  starting: boolean;
  startError: string | null;
}

const initialState: SharePointState = {
  sites: null,
  drivesBySite: {},
  childrenByKey: {},
  sitesLoading: false,
  sitesError: null,
  drivesLoading: false,
  drivesError: null,
  childrenLoading: false,
  childrenError: null,
  itemWarning: null,
  configError: null,
  selection: [],
  jobsById: {},
  jobList: [],
  currentJobId: undefined,
  starting: false,
  startError: null,
};

// ---------------------------------------------------------------------------
// Error helpers
// ---------------------------------------------------------------------------

/** A minimal shape for axios-style errors so we can detect the 409 config error. */
interface HttpErrorLike {
  response?: { status?: number; data?: { error?: string; message?: string; jobId?: string } };
  message?: string;
}

function messageOf(err: unknown, fallback: string): string {
  const e = err as HttpErrorLike;
  return e?.response?.data?.message || e?.message || fallback;
}

/** True when the backend returned a 409 CONFIG_ERROR (credentials/consent). */
function isConfigError(err: unknown): boolean {
  const e = err as HttpErrorLike;
  return e?.response?.status === 409 || e?.response?.data?.error === 'CONFIG_ERROR';
}

/** The browse endpoints return a 200 with an ITEM_INACCESSIBLE payload (R3.5). */
function inaccessiblePayload<T extends { error?: string; message?: string }>(
  data: T,
): string | null {
  return data && data.error === 'ITEM_INACCESSIBLE' ? data.message ?? 'Item inaccessible' : null;
}

/** rejectWithValue payload distinguishing a config error from an ordinary one. */
interface ThunkError {
  message: string;
  config: boolean;
}

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

/**
 * Fetch the tenant's SharePoint sites.
 * GET /sharepoint/sites
 * Requirements: 3.1
 */
export const fetchSites = createAsyncThunk<SharePointSite[], void, { rejectValue: ThunkError }>(
  'sharepoint/fetchSites',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<{ sites: SharePointSite[]; error?: string; message?: string }>(
        '/sharepoint/sites',
      );
      const warn = inaccessiblePayload(res.data);
      if (warn) return rejectWithValue({ message: warn, config: false });
      return res.data.sites ?? [];
    } catch (err) {
      return rejectWithValue({ message: messageOf(err, 'Failed to load sites'), config: isConfigError(err) });
    }
  },
);

/**
 * Fetch the document libraries (drives) of a site.
 * GET /sharepoint/sites/{siteId}/drives
 * Requirements: 3.2
 */
export const fetchDrives = createAsyncThunk<
  { siteId: string; drives: SharePointDrive[] },
  string,
  { rejectValue: ThunkError }
>('sharepoint/fetchDrives', async (siteId, { rejectWithValue }) => {
  try {
    const res = await apiClient.get<{ drives: SharePointDrive[]; error?: string; message?: string }>(
      `/sharepoint/sites/${encodeURIComponent(siteId)}/drives`,
    );
    const warn = inaccessiblePayload(res.data);
    if (warn) return rejectWithValue({ message: warn, config: false });
    return { siteId, drives: res.data.drives ?? [] };
  } catch (err) {
    return rejectWithValue({ message: messageOf(err, 'Failed to load libraries'), config: isConfigError(err) });
  }
});

/**
 * Fetch the children (folders + files) of a drive root or subfolder.
 * GET /sharepoint/drives/{driveId}/children?itemId=
 * Requirements: 3.3, 3.4, 3.5
 */
export const fetchChildren = createAsyncThunk<
  { key: ChildrenKey; items: SharePointItem[] },
  { driveId: string; itemId?: string },
  { rejectValue: ThunkError }
>('sharepoint/fetchChildren', async ({ driveId, itemId }, { rejectWithValue }) => {
  try {
    const query = itemId ? `?itemId=${encodeURIComponent(itemId)}` : '';
    const res = await apiClient.get<{ items: SharePointItem[]; error?: string; message?: string }>(
      `/sharepoint/drives/${encodeURIComponent(driveId)}/children${query}`,
    );
    const warn = inaccessiblePayload(res.data);
    if (warn) return rejectWithValue({ message: warn, config: false });
    return { key: childrenKey(driveId, itemId), items: res.data.items ?? [] };
  } catch (err) {
    return rejectWithValue({ message: messageOf(err, 'Failed to load folder'), config: isConfigError(err) });
  }
});

/** Argument to {@link startImport}: the selection plus an optional destination. */
export interface StartImportArg {
  selectedFolders: SelectedFolder[];
  /**
   * Optional MediaManager destination folder. When set, imported files land
   * under this folder (the selected SharePoint folder's tree is recreated
   * beneath it). Accepts a `string[]` path or a slash-joined string; empty is
   * omitted from the request so the SharePoint structure is mirrored as-is.
   */
  destination?: string[] | string;
}

/** rejectWithValue payload for a start-import failure, incl. the 409 in-progress case. */
export interface StartImportError {
  message: string;
  /** Set when the backend returned 409 IMPORT_IN_PROGRESS with the existing job. */
  inProgressJobId?: string;
}

/** Normalise a destination (string[] or slash string) to a trimmed slash path, or ''. */
function destinationToStr(destination?: string[] | string): string {
  if (!destination) return '';
  const raw = Array.isArray(destination) ? destination.join('/') : destination;
  return raw
    .split('/')
    .map((s) => s.trim())
    .filter(Boolean)
    .join('/');
}

/**
 * Start an import job for the selected folders.
 * POST /sharepoint/imports -> 202 { jobId }
 * When a queued/running import already exists the backend returns
 * 409 { error:'IMPORT_IN_PROGRESS', message, jobId } — surfaced as a start
 * error with the existing jobId (not a generic failure).
 * Requirements: 4.1, 4.3
 */
export const startImport = createAsyncThunk<
  string,
  StartImportArg | SelectedFolder[],
  { rejectValue: StartImportError }
>('sharepoint/startImport', async (arg, { rejectWithValue }) => {
  // Backward-compatible: accept either the raw selection array or the arg object.
  const selectedFolders = Array.isArray(arg) ? arg : arg.selectedFolders;
  const destination = Array.isArray(arg) ? undefined : arg.destination;

  // Client-side guard mirrors the backend 400 on empty selection (R4.3).
  if (!selectedFolders || selectedFolders.length === 0) {
    return rejectWithValue({ message: 'Select at least one folder to import' });
  }
  try {
    const body: Record<string, unknown> = { ...buildImportPayload(selectedFolders) };
    const destStr = destinationToStr(destination);
    if (destStr) body.destinationPathStr = destStr;
    const res = await apiClient.post<{ jobId: string }>('/sharepoint/imports', body);
    return res.data.jobId;
  } catch (err) {
    const e = err as HttpErrorLike;
    // 409 IMPORT_IN_PROGRESS: an active job already exists — attach its id.
    if (e?.response?.status === 409 && e?.response?.data?.error === 'IMPORT_IN_PROGRESS') {
      return rejectWithValue({
        message: messageOf(err, 'An import is already in progress'),
        inProgressJobId: (e.response?.data as { jobId?: string })?.jobId,
      });
    }
    return rejectWithValue({ message: messageOf(err, 'Failed to start import') });
  }
});

/**
 * Poll a single import job's status.
 * GET /sharepoint/imports/{jobId}
 * Requirements: 8.1
 */
export const fetchImportStatus = createAsyncThunk<ImportJobStatus, string, { rejectValue: string }>(
  'sharepoint/fetchImportStatus',
  async (jobId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<ImportJobStatus>(
        `/sharepoint/imports/${encodeURIComponent(jobId)}`,
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(messageOf(err, 'Failed to fetch import status'));
    }
  },
);

/**
 * List this admin's recent import jobs.
 * GET /sharepoint/imports
 * Requirements: 8.1
 */
export const fetchImports = createAsyncThunk<ImportJobSummary[], void, { rejectValue: string }>(
  'sharepoint/fetchImports',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<{ jobs: ImportJobSummary[] }>('/sharepoint/imports');
      return res.data.jobs ?? [];
    } catch (err) {
      return rejectWithValue(messageOf(err, 'Failed to list imports'));
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const sharepointSlice = createSlice({
  name: 'sharepoint',
  initialState,
  reducers: {
    toggleFolderSelection(state, action: { payload: SelectedFolder }) {
      const folder = action.payload;
      const present = state.selection.some(
        (s) => s.driveId === folder.driveId && s.itemId === folder.itemId,
      );
      state.selection = present
        ? removeSelection(state.selection, folder)
        : addSelection(state.selection, folder);
    },
    removeFolderSelection(state, action: { payload: { driveId: string; itemId: string } }) {
      state.selection = removeSelection(state.selection, action.payload);
    },
    clearSelection(state) {
      state.selection = [];
    },
    clearErrors(state) {
      state.sitesError = null;
      state.drivesError = null;
      state.childrenError = null;
      state.itemWarning = null;
      state.configError = null;
      state.startError = null;
    },
    clearItemWarning(state) {
      state.itemWarning = null;
    },
    setCurrentJob(state, action: { payload: string | undefined }) {
      state.currentJobId = action.payload;
      // Persist only while the (known) job is non-terminal; otherwise clear so a
      // later visit doesn't re-pin a finished job.
      const job = action.payload ? state.jobsById[action.payload] : undefined;
      if (action.payload && (!job || !isTerminalState(job.state))) {
        persistCurrentJobId(action.payload);
      } else {
        persistCurrentJobId(undefined);
      }
    },
  },
  extraReducers: (builder) => {
    // fetchSites
    builder
      .addCase(fetchSites.pending, (state) => {
        state.sitesLoading = true;
        state.sitesError = null;
      })
      .addCase(fetchSites.fulfilled, (state, action) => {
        state.sitesLoading = false;
        state.sites = action.payload;
        state.configError = null;
      })
      .addCase(fetchSites.rejected, (state, action) => {
        state.sitesLoading = false;
        const payload = action.payload;
        if (payload?.config) {
          state.configError = payload.message;
        } else {
          state.sitesError = payload?.message ?? 'Failed to load sites';
        }
      });

    // fetchDrives
    builder
      .addCase(fetchDrives.pending, (state) => {
        state.drivesLoading = true;
        state.drivesError = null;
      })
      .addCase(fetchDrives.fulfilled, (state, action) => {
        state.drivesLoading = false;
        state.drivesBySite[action.payload.siteId] = action.payload.drives;
      })
      .addCase(fetchDrives.rejected, (state, action) => {
        state.drivesLoading = false;
        const payload = action.payload;
        if (payload?.config) {
          state.configError = payload.message;
        } else {
          state.drivesError = payload?.message ?? 'Failed to load libraries';
        }
      });

    // fetchChildren
    builder
      .addCase(fetchChildren.pending, (state) => {
        state.childrenLoading = true;
        state.childrenError = null;
        state.itemWarning = null;
      })
      .addCase(fetchChildren.fulfilled, (state, action) => {
        state.childrenLoading = false;
        state.childrenByKey[action.payload.key] = action.payload.items;
      })
      .addCase(fetchChildren.rejected, (state, action) => {
        state.childrenLoading = false;
        const payload = action.payload;
        if (payload?.config) {
          state.configError = payload.message;
        } else {
          // A per-item inaccessible (R3.5) is a soft warning, not a hard error.
          state.childrenError = payload?.message ?? 'Failed to load folder';
          state.itemWarning = payload?.message ?? null;
        }
      });

    // startImport
    builder
      .addCase(startImport.pending, (state) => {
        state.starting = true;
        state.startError = null;
      })
      .addCase(startImport.fulfilled, (state, action) => {
        state.starting = false;
        state.currentJobId = action.payload;
        persistCurrentJobId(action.payload);
        // Seed a queued job status so the progress view has something to render.
        state.jobsById[action.payload] = {
          jobId: action.payload,
          state: 'queued',
          totals: { discovered: 0, imported: 0, skipped: 0, failed: 0, alreadyPresent: 0 },
        };
        // Clear the selection now that the job has been submitted.
        state.selection = [];
      })
      .addCase(startImport.rejected, (state, action) => {
        state.starting = false;
        const payload = action.payload;
        state.startError = payload?.message ?? 'Failed to start import';
        // 409 IMPORT_IN_PROGRESS: pin the already-running job so the user sees it.
        if (payload?.inProgressJobId) {
          state.currentJobId = payload.inProgressJobId;
          persistCurrentJobId(payload.inProgressJobId);
          if (!state.jobsById[payload.inProgressJobId]) {
            state.jobsById[payload.inProgressJobId] = {
              jobId: payload.inProgressJobId,
              state: 'running',
              totals: { discovered: 0, imported: 0, skipped: 0, failed: 0, alreadyPresent: 0 },
            };
          }
        }
      });

    // fetchImportStatus
    builder.addCase(fetchImportStatus.fulfilled, (state, action) => {
      state.jobsById[action.payload.jobId] = action.payload;
      // Once the current job finishes, stop pinning it across visits (it stays
      // viewable via the recent list).
      if (
        state.currentJobId === action.payload.jobId &&
        isTerminalState(action.payload.state)
      ) {
        persistCurrentJobId(undefined);
      }
    });

    // fetchImports
    builder.addCase(fetchImports.fulfilled, (state, action) => {
      state.jobList = action.payload;
    });
  },
});

export const {
  toggleFolderSelection,
  removeFolderSelection,
  clearSelection,
  clearErrors,
  clearItemWarning,
  setCurrentJob,
} = sharepointSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

type WithSharePoint = { sharepoint: SharePointState };

export const selectSites = (s: WithSharePoint) => s.sharepoint.sites;
export const selectSitesLoading = (s: WithSharePoint) => s.sharepoint.sitesLoading;
export const selectSitesError = (s: WithSharePoint) => s.sharepoint.sitesError;
export const selectDrivesBySite = (s: WithSharePoint) => s.sharepoint.drivesBySite;
export const selectDrivesLoading = (s: WithSharePoint) => s.sharepoint.drivesLoading;
export const selectDrivesError = (s: WithSharePoint) => s.sharepoint.drivesError;
export const selectChildrenByKey = (s: WithSharePoint) => s.sharepoint.childrenByKey;
export const selectChildrenLoading = (s: WithSharePoint) => s.sharepoint.childrenLoading;
export const selectChildrenError = (s: WithSharePoint) => s.sharepoint.childrenError;
export const selectItemWarning = (s: WithSharePoint) => s.sharepoint.itemWarning;
export const selectConfigError = (s: WithSharePoint) => s.sharepoint.configError;
export const selectSelection = (s: WithSharePoint) => s.sharepoint.selection;
export const selectStarting = (s: WithSharePoint) => s.sharepoint.starting;
export const selectStartError = (s: WithSharePoint) => s.sharepoint.startError;
export const selectCurrentJobId = (s: WithSharePoint) => s.sharepoint.currentJobId;
export const selectJobsById = (s: WithSharePoint) => s.sharepoint.jobsById;
export const selectJobList = (s: WithSharePoint) => s.sharepoint.jobList;
export const selectCurrentJob = (s: WithSharePoint): ImportJobStatus | undefined =>
  s.sharepoint.currentJobId ? s.sharepoint.jobsById[s.sharepoint.currentJobId] : undefined;

/**
 * The active (queued/running) job the admin has, if any — derived from the
 * current job and the recent job list. Used to disable Start while an import is
 * in progress and to auto-resume the live progress card.
 */
export const selectActiveJob = (s: WithSharePoint): { jobId: string; state: ImportState } | undefined => {
  const current = selectCurrentJob(s);
  if (current && !isTerminalState(current.state)) {
    return { jobId: current.jobId, state: current.state };
  }
  const fromList = s.sharepoint.jobList.find((j) => !isTerminalState(j.state));
  return fromList ? { jobId: fromList.jobId, state: fromList.state } : undefined;
};

export default sharepointSlice.reducer;
